import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ShoppingBag, Plus, Minus, Search, ChevronRight, MapPin, Info, Globe, X, Check } from 'lucide-react';
import { menuAPI, orderAPI, tablesAPI, settingsAPI } from '../utils/api';
import { i18n, formatPrice } from '../utils/translations';
import { setSEO } from '../utils/seo';

// Language options with flags
const LANGUAGES = [
  { code: 'en', name: 'English', flag: 'US' },
  { code: 'vi', name: 'Tiếng Việt', flag: 'VN' },
  { code: 'ru', name: 'Русский', flag: 'RU' },
  { code: 'fr', name: 'Français', flag: 'FR' },
  { code: 'ja', name: '日本語', flag: 'JP' },
  { code: 'ko', name: '한국어', flag: 'KR' },
  { code: 'zh', name: '中文', flag: 'CN' },
  { code: 'de', name: 'Deutsch', flag: 'DE' },
  { code: 'es', name: 'Español', flag: 'ES' },
  { code: 'th', name: 'ไทย', flag: 'TH' },
  { code: 'it', name: 'Italiano', flag: 'IT' },
  { code: 'pt', name: 'Português', flag: 'PT' },
  { code: 'nl', name: 'Nederlands', flag: 'NL' },
  { code: 'id', name: 'Indonesia', flag: 'ID' },
  { code: 'ar', name: 'العربية', flag: 'SA' },
  { code: 'hi', name: 'हिन्दी', flag: 'IN' },
];

// Beverage categories for suggestion logic
const BEVERAGE_CATEGORIES = [
  "Smoothies", "Juice", "Cocktails", "Long Drinks", "Beer", 
  "Soft Drinks", "Wine", "Hard Liquor", "Coffee"
];

const CustomerOrder = () => {
  const [searchParams] = useSearchParams();
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCat, setCurrentCat] = useState('All');
  const [currentLang, setCurrentLang] = useState(LANGUAGES[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tray, setTray] = useState([]);
  const [selectedTable, setSelectedTable] = useState(searchParams.get('table') || '');
  const [pendingItem, setPendingItem] = useState(null);
  const [chefNote, setChefNote] = useState('');
  const [showPrepModal, setShowPrepModal] = useState(false);
  const [showTrayModal, setShowTrayModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showIngredientModal, setShowIngredientModal] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isLangModalOpen, setIsLangModalOpen] = useState(false);
  const [selectedDish, setSelectedDish] = useState(null);
  const [suggestedItems, setSuggestedItems] = useState([]);
  const [promotedItems, setPromotedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [lastOrderNumber, setLastOrderNumber] = useState(null);
  const [settings, setSettings] = useState(null);
  // Active tables from the DB — the customer can only order for one of these.
  const [activeTables, setActiveTables] = useState([]);
  const [tablesLoaded, setTablesLoaded] = useState(false);
  const tableFromQR = searchParams.get('table');
  
  const submitLockRef = useRef(false);

  const t = i18n[currentLang.code] || i18n.en;

  // Load settings
  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  // Load menu from API
  const loadMenu = async () => {
    try {
      const response = await menuAPI.getAll();
      setMenuItems(response.data);
      const uniqueCats = [...new Set(response.data.map(item => item.category))];
      setCategories(uniqueCats);
      
      const promoted = response.data.filter(item => item.is_promoted && item.available);
      setPromotedItems(promoted);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading menu:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    setSEO({ title: 'Order | Benz Eatery Phu Quoc', description: 'Place your order at Benz Eatery — scan QR at your table to order fresh Vietnamese cuisine and international dishes.' });
    loadMenu();
    loadSettings();
    // Load active tables so we can validate the ?table= QR param and prevent
    // customers from placing orders with arbitrary table numbers.
    tablesAPI.getAll()
      .then(res => {
        const active = (res.data || []).filter(t => t.is_active !== false);
        setActiveTables(active);
      })
      .catch(() => setActiveTables([]))
      .finally(() => setTablesLoaded(true));
  }, []);

  // Derived: is the table that was scanned (or typed) actually in the DB?
  const isTableValid = useMemo(() => {
    if (!tablesLoaded || !selectedTable) return false;
    const t = (selectedTable || '').trim();
    return activeTables.some(x => String(x.number).trim() === t);
  }, [activeTables, tablesLoaded, selectedTable]);

  // Get dish name in current language
  const getDishName = (item) => item.name[currentLang.code] || item.name.en;
  const getCategoryLabel = (cat) => t[cat.toLowerCase()] || cat;

  const isBeverageCategory = (category) => BEVERAGE_CATEGORIES.includes(category);

  // Filter items by category and search
  const filteredItems = useMemo(() => {
    const availableItems = menuItems.filter(item => item.available !== false);
    return availableItems.filter(item => {
      const matchesCategory = currentCat === 'All' || item.category === currentCat;
      const dishName = getDishName(item).toLowerCase();
      const matchesSearch = dishName.includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [menuItems, currentCat, searchTerm, currentLang]);

  // Smart suggestions based on cart contents
  const getSmartSuggestions = () => {
    const trayCategories = tray.map(item => item.category);
    const hasFoodInTray = trayCategories.some(cat => !isBeverageCategory(cat));
    const hasDrinksInTray = trayCategories.some(cat => isBeverageCategory(cat));
    const trayIds = tray.map(item => item.id);

    let suggestions = [];
    const availablePromoted = promotedItems.filter(item => !trayIds.includes(item.id) && item.available !== false);
    const availableMenuItems = menuItems.filter(item => item.available !== false);
    
    if (hasFoodInTray && !hasDrinksInTray) {
      const promotedDrinks = availablePromoted.filter(item => isBeverageCategory(item.category));
      const otherDrinks = availableMenuItems
        .filter(item => isBeverageCategory(item.category) && !trayIds.includes(item.id) && !promotedDrinks.find(p => p.id === item.id))
        .sort(() => Math.random() - 0.5);
      suggestions = [...promotedDrinks, ...otherDrinks].slice(0, 6);
    } else if (hasDrinksInTray && !hasFoodInTray) {
      const promotedFood = availablePromoted.filter(item => !isBeverageCategory(item.category));
      const otherFood = availableMenuItems
        .filter(item => !isBeverageCategory(item.category) && !trayIds.includes(item.id) && !promotedFood.find(p => p.id === item.id))
        .sort(() => Math.random() - 0.5);
      suggestions = [...promotedFood, ...otherFood].slice(0, 6);
    } else if (hasFoodInTray && hasDrinksInTray) {
      const promotedExtras = availablePromoted.filter(item => 
        item.category === 'Dessert' || item.category === 'Appetizer'
      );
      const otherExtras = availableMenuItems
        .filter(item => (item.category === 'Dessert' || item.category === 'Appetizer') && 
                !trayIds.includes(item.id) && !promotedExtras.find(p => p.id === item.id))
        .sort(() => Math.random() - 0.5);
      suggestions = [...promotedExtras, ...otherExtras].slice(0, 6);
    }

    return suggestions;
  };

  // View ingredients modal
  const viewIngredients = (item, e) => {
    e.stopPropagation();
    setSelectedDish(item);
    setShowIngredientModal(true);
  };

  const openPrepModal = (item) => {
    if (settings?.enable_prep_suggestions === false) {
      quickAddItem(item);
      return;
    }
    setPendingItem(item);
    setChefNote('');
    setShowPrepModal(true);
  };

  const confirmItem = () => {
    const itemInstance = {
      ...pendingItem,
      instanceId: Date.now(),
      quantity: 1,
      suggestions: [],
      notes: chefNote
    };
    setTray([...tray, itemInstance]);
    setShowPrepModal(false);
    setPendingItem(null);
  };

  const quickAddItem = (item, e) => {
    if (e) e.stopPropagation();
    const existingIndex = tray.findIndex(t => t.id === item.id && (t.suggestions?.length ?? 0) === 0 && !t.notes);
    if (existingIndex > -1) {
      const newTray = [...tray];
      newTray[existingIndex].quantity += 1;
      setTray(newTray);
    } else {
      const itemInstance = {
        ...item,
        instanceId: Date.now(),
        quantity: 1,
        suggestions: [],
        notes: ''
      };
      setTray([...tray, itemInstance]);
    }
  };

  const adjustQty = (instanceId, delta) => {
    const idx = tray.findIndex(t => t.instanceId === instanceId);
    if (idx === -1) return;
    const newTray = [...tray];
    newTray[idx].quantity += delta;
    if (newTray[idx].quantity <= 0) {
      newTray.splice(idx, 1);
    }
    setTray(newTray);
  };

  const removeItem = (instanceId) => {
    setTray(tray.filter(t => t.instanceId !== instanceId));
  };

  const getItemQtyInTray = (itemId) => {
    return tray.filter(t => t.id === itemId).reduce((sum, t) => sum + t.quantity, 0);
  };

  const getTrayTotal = () => tray.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const getTrayCount = () => tray.reduce((sum, item) => sum + item.quantity, 0);

  const proceedToOrder = () => {
    const suggestions = getSmartSuggestions();
    if (suggestions.length > 0) {
      setSuggestedItems(suggestions);
      setShowSuggestionModal(true);
    } else {
      setShowTrayModal(true);
    }
  };

  const skipSuggestions = () => {
    setShowSuggestionModal(false);
    setShowTrayModal(true);
  };

  const placeOrder = async () => {
    if (!selectedTable || tray.length === 0 || isSubmitting || submitLockRef.current) return;
    // Safety: never let the customer place an order for an invalid table
    // (e.g. typed in URL query, stale cached QR, or race before tables loaded).
    if (!isTableValid) {
      alert(t.invalidTableShort || 'Invalid table. Please scan the QR on your table.');
      return;
    }
    
    submitLockRef.current = true;
    setIsSubmitting(true);
    
    try {
      const orderData = {
        items: tray.map(item => {
          const suggestions = Array.isArray(item.suggestions) ? item.suggestions : [];
          const notes = (item.notes || '').trim();
          const joined = suggestions.join(', ');
          const special_note = notes
            ? (joined ? `${joined} | ${notes}` : notes)
            : joined;
          return {
            menu_item_id: item.id,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            category: item.category,
            selected_options: suggestions,
            special_note,
          };
        }),
        table: selectedTable,
        total: getTrayTotal(),
        customer_email: customerEmail || ''
      };
      
      const response = await orderAPI.create(orderData);
      const orderNum = response.data.order_number;
      setLastOrderNumber(orderNum);

      // Save to localStorage for repeat order
      try {
        const savedOrder = tray.map(item => ({
          id: item.id, name: item.name, price: item.price,
          category: item.category, quantity: item.quantity,
          image_url: item.image_url,
        }));
        localStorage.setItem('benz_last_order', JSON.stringify(savedOrder));
        localStorage.setItem('benz_last_order_time', new Date().toISOString());
      } catch (e) {}

      setTray([]);
      setSelectedTable(tableFromQR || '');
      setShowTrayModal(false);

      // Show a simple in-place confirmation — the customer-side live tracker
      // page has been removed per request. Staff will handle the rest.
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 4000);
    } catch (error) {
      console.error('Error placing order:', error);
      alert('Failed to place order. Please try again.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        submitLockRef.current = false;
      }, 2000);
    }
  };

  const submitEmail = async () => {
    setShowEmailModal(false);
    setShowSuccessModal(true);
    setCustomerEmail('');
    setTimeout(() => setShowSuccessModal(false), 3000);
  };

  const skipEmail = () => {
    setShowEmailModal(false);
    setShowSuccessModal(true);
    setTimeout(() => setShowSuccessModal(false), 3000);
  };

  const updateTrayItemNote = (instanceId, note) => {
    setTray(tray.map(item => 
      item.instanceId === instanceId 
        ? { ...item, notes: note }
        : item
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFDFD]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Loading menu...</p>
        </div>
      </div>
    );
  }

  // Block access if the customer did not arrive via a QR scan.
  // The restaurant explicitly wants orders to be placed only from a scanned
  // table QR — so visiting /order directly (without ?table=...) is not allowed.
  if (!tableFromQR) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFDFD] px-6" data-testid="scan-qr-required">
        <div className="max-w-sm w-full text-center bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-orange-50 flex items-center justify-center text-orange-500">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <path d="M14 14h3v3h-3zM18 18h3v3h-3zM14 19h2"/>
            </svg>
          </div>
          <h1 className="text-xl font-black text-slate-900 mb-2" data-testid="scan-qr-title">
            {t.scanQrTitle || 'Please scan the QR at your table'}
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed mb-6">
            {t.scanQrBody || 'To place an order, scan the QR code printed on your table. This links your order to the right table so our staff can serve you fast.'}
          </p>
          <div className="flex flex-col gap-2">
            <Link
              to="/menu"
              className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white font-bold text-sm py-3 rounded-2xl active:scale-95 transition-transform"
              data-testid="scan-qr-browse-menu"
            >
              {t.browseMenu || 'Browse Menu'}
            </Link>
            <Link
              to="/"
              className="w-full inline-flex items-center justify-center gap-2 text-slate-500 text-sm py-2"
              data-testid="scan-qr-back-home"
            >
              {t.backToHome || 'Back to home'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] font-sans text-slate-900 pb-32">
      {/* Search Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100 px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                Benz Eatery
              </h1>
              <p className="text-slate-400 text-[11px] font-bold uppercase tracking-widest flex items-center gap-1">
                <MapPin size={12} className="text-orange-500" /> {t.premium || 'Phu Quoc Premium'}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setIsLangModalOpen(true)}
                className="group flex items-center gap-2 bg-slate-50 border border-slate-100 pl-2 pr-3 py-1.5 rounded-2xl hover:bg-white hover:border-orange-200 transition-all active:scale-95 shadow-sm"
                data-testid="language-selector-btn"
              >
                <div className="bg-white rounded-lg p-1 shadow-sm border border-slate-100 group-hover:border-orange-100 transition-colors">
                  <span className="text-base leading-none block">{currentLang.flag}</span>
                </div>
                <div className="flex flex-col items-start leading-tight">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Language</span>
                  <span className="text-xs font-black text-slate-700">{currentLang.name}</span>
                </div>
                <Globe size={14} className="text-slate-300 group-hover:text-orange-500 transition-colors ml-1" />
              </button>
            </div>
          </div>
          
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-orange-500" size={18} />
            <input 
              type="text" 
              placeholder={t.searchPlaceholder || "Search for dishes..."} 
              className="w-full bg-slate-50 border border-slate-100 rounded-[1.25rem] py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-orange-100 focus:bg-white text-slate-700 transition-all placeholder:text-slate-400 font-medium"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
            />
          </div>
        </div>
      </header>

      {/* Categories Bar */}
      <div className="sticky top-[137px] z-40 bg-white/95 backdrop-blur-sm py-3 border-b border-slate-50 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 overflow-x-auto no-scrollbar flex items-center gap-2">
          <button
            onClick={() => setCurrentCat('All')}
            className={`whitespace-nowrap px-5 py-2 rounded-xl text-sm font-bold transition-all duration-200 ${
              currentCat === 'All' 
              ? 'bg-slate-900 text-white shadow-lg shadow-slate-200' 
              : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
            }`}
            data-testid="category-all"
          >
            {t.all || 'All'}
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setCurrentCat(cat)}
              className={`whitespace-nowrap px-5 py-2 rounded-xl text-sm font-bold transition-all duration-200 ${
                currentCat === cat 
                ? 'bg-slate-900 text-white shadow-lg shadow-slate-200' 
                : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {getCategoryLabel(cat)}
            </button>
          ))}
        </div>
      </div>

      {/* Product List */}
      <main className="max-w-2xl mx-auto px-4 mt-6">
        {/* Repeat Order */}
        {(() => {
          try {
            const saved = JSON.parse(localStorage.getItem('benz_last_order') || '[]');
            const savedTime = localStorage.getItem('benz_last_order_time');
            if (!saved.length) return null;
            const timeAgo = savedTime ? Math.floor((Date.now() - new Date(savedTime).getTime()) / 86400000) : null;
            return (
              <div className="mb-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4" data-testid="repeat-order-section">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800">{t.repeatOrder || 'Order Again'}</h3>
                    <p className="text-xs text-slate-500">{timeAgo !== null && timeAgo < 30 ? `${timeAgo === 0 ? 'Today' : `${timeAgo}d ago`} · ` : ''}{saved.length} {t.items || 'items'}</p>
                  </div>
                  <button
                    onClick={() => {
                      const newItems = saved.map(s => {
                        const menuItem = menuItems.find(m => m.id === s.id);
                        if (!menuItem) return null;
                        return { ...menuItem, quantity: s.quantity || 1, instanceId: `${menuItem.id}_repeat_${Date.now()}_${Math.random()}`, suggestions: [], notes: '' };
                      }).filter(Boolean);
                      if (newItems.length) { setTray(prev => [...prev, ...newItems]); }
                    }}
                    className="px-4 py-2 bg-amber-500 text-white text-xs font-bold rounded-xl hover:bg-amber-600 transition-all"
                    data-testid="repeat-order-btn"
                  >
                    {t.addAll || 'Add All'}
                  </button>
                </div>
                <div className="flex gap-2 overflow-x-auto no-scrollbar">
                  {saved.slice(0, 5).map((s, i) => (
                    <div key={i} className="flex-shrink-0 bg-white rounded-xl px-3 py-2 border border-amber-100 text-xs">
                      <span className="font-bold text-slate-700">{typeof s.name === 'string' ? s.name : (s.name?.en || s.name?.vi || 'Item')}</span>
                      <span className="text-amber-600 ml-1">x{s.quantity || 1}</span>
                    </div>
                  ))}
                  {saved.length > 5 && <div className="flex-shrink-0 bg-white rounded-xl px-3 py-2 border border-amber-100 text-xs text-slate-400">+{saved.length - 5}</div>}
                </div>
              </div>
            );
          } catch { return null; }
        })()}
        <div className="space-y-4">
          {filteredItems.map(item => {
            const qtyInTray = getItemQtyInTray(item.id);
            const hasIngredients = item.ingredients && item.ingredients.trim();
            const isPromoted = item.is_promoted;
            
            return (
              <div 
                key={item.id} 
                className={`flex bg-white rounded-[2rem] p-3 border shadow-sm hover:shadow-md transition-all duration-300 group ${
                  isPromoted ? 'border-orange-200 ring-1 ring-orange-100' : 'border-slate-100'
                }`}
                data-testid={`menu-item-${item.id}`}
              >
                <div 
                  className="relative w-32 h-32 flex-shrink-0 cursor-pointer"
                  onClick={() => openPrepModal(item)}
                >
                  {item.image_url ? (
                    <img 
                      src={item.image_url} 
                      alt={getDishName(item)}
                      className="w-full h-full object-cover rounded-[1.5rem] shadow-inner transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-slate-100 rounded-[1.5rem] flex items-center justify-center text-4xl"></div>
                  )}
                  
                  {isPromoted && (
                    <div className="absolute -top-1 -left-1 bg-white p-1 rounded-full shadow-md">
                      <div className="bg-orange-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter">
                        {t.mustTry || 'Best Choice'}
                      </div>
                    </div>
                  )}
                  
                  {/* Quantity Badge */}
                  {qtyInTray > 0 && (
                    <div className="absolute -top-1 -right-1 w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-black shadow-lg">
                      {qtyInTray}
                    </div>
                  )}
                  
                  {/* Ingredients Button */}
                  {hasIngredients && (
                    <button
                      onClick={(e) => viewIngredients(item, e)}
                      className="absolute bottom-2 left-2 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
                      data-testid={`ingredients-${item.id}`}
                    >
                      <Info size={14} className="text-slate-500" />
                    </button>
                  )}
                </div>

                <div className="ml-4 flex-grow flex flex-col justify-between py-1">
                  <div>
                    <div className="flex justify-between items-start">
                      <h3 className="font-bold text-lg text-slate-900 leading-tight group-hover:text-orange-500 transition-colors line-clamp-2 pr-2">
                        {getDishName(item)}
                      </h3>
                    </div>
                    {item.description && (
                      <p className="text-slate-400 text-xs mt-1 line-clamp-2 pr-4 leading-relaxed font-medium">
                        {item.description[currentLang.code] || item.description.en || item.description}
                      </p>
                    )}
                    <div className="flex gap-2 mt-2">
                      <span className="text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded-md">
                        #{item.category}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xl font-black text-slate-900">
                      {formatPrice(item.price)}
                    </span>

                    {qtyInTray > 0 ? (
                      <div className="flex items-center bg-orange-500 rounded-full px-1 py-1 gap-3 shadow-lg shadow-orange-100">
                        <button 
                          onClick={(e) => { e.stopPropagation(); adjustQty(tray.find(t => t.id === item.id)?.instanceId, -1); }}
                          className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full text-white hover:bg-white/30 transition-all"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="font-bold text-white text-sm">{qtyInTray}</span>
                        <button 
                          onClick={(e) => quickAddItem(item, e)}
                          className="w-8 h-8 flex items-center justify-center bg-white rounded-full text-orange-500 shadow-sm active:scale-90 transition-all"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    ) : (
                      <button 
                        onClick={(e) => quickAddItem(item, e)}
                        className="bg-slate-900 text-white p-3 rounded-2xl shadow-lg shadow-slate-100 hover:bg-orange-500 hover:shadow-orange-200 transition-all duration-300 active:scale-90"
                        data-testid={`add-${item.id}`}
                      >
                        <Plus size={20} strokeWidth={3} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {filteredItems.length === 0 && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4"></div>
              <p className="text-slate-400 font-medium">{t.noItemsFound || 'No items found'}</p>
            </div>
          )}
        </div>
      </main>

      {/* Language Selection Modal */}
      {isLangModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center">
          <div 
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsLangModalOpen(false)}
          />
          <div className="relative w-full max-w-md bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] p-6 shadow-2xl animate-slide-up">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-black text-slate-900">{t.selectLanguage || 'Select Language'}</h2>
              <button 
                onClick={() => setIsLangModalOpen(false)}
                className="p-2 bg-slate-50 rounded-full text-slate-400 hover:text-slate-900 transition-all"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="grid grid-cols-1 gap-2 max-h-[60vh] overflow-y-auto no-scrollbar pb-4">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setCurrentLang(lang);
                    setIsLangModalOpen(false);
                  }}
                  className={`flex items-center justify-between p-4 rounded-2xl transition-all border ${
                    currentLang.code === lang.code 
                    ? 'bg-orange-50 border-orange-200' 
                    : 'bg-slate-50 border-slate-100 hover:border-slate-300'
                  }`}
                  data-testid={`lang-${lang.code}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-2xl">{lang.flag}</span>
                    <span className={`font-bold ${currentLang.code === lang.code ? 'text-orange-600' : 'text-slate-700'}`}>
                      {lang.name}
                    </span>
                  </div>
                  {currentLang.code === lang.code && (
                    <div className="bg-orange-500 p-1 rounded-full text-white">
                      <Check size={14} strokeWidth={4} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modern Checkout Bar */}
      {getTrayCount() > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-2.5rem)] max-w-lg z-50">
          <div 
            className="bg-slate-900/95 backdrop-blur-xl text-white rounded-[2.25rem] p-2.5 flex items-center justify-between shadow-2xl border border-white/5 cursor-pointer active:scale-[0.98] transition-transform"
            onClick={proceedToOrder}
            data-testid="view-cart-btn"
          >
            <div className="flex items-center gap-3 ml-2">
              <div className="relative bg-orange-500 w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30">
                <ShoppingBag size={22} />
                <span className="absolute -top-1 -right-1 bg-white text-slate-900 text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center border-2 border-slate-900">
                  {getTrayCount()}
                </span>
              </div>
              <div>
                <span className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">{t.totalCost || 'Total Cost'}</span>
                <span className="text-base font-black tracking-tight">{formatPrice(getTrayTotal())}</span>
              </div>
            </div>
            <button className="bg-white text-slate-900 font-black px-8 py-3.5 rounded-[1.5rem] flex items-center gap-2 hover:bg-orange-500 hover:text-white transition-all group shadow-sm">
              {t.checkout || 'Checkout'}
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      )}

      {/* SMART SUGGESTION MODAL - Must Try Items */}
      {showSuggestionModal && suggestedItems.length > 0 && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-[2.5rem] rounded-t-[2.5rem] max-h-[85vh] overflow-hidden animate-slide-up">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-4">
              <h3 className="text-xl font-black text-white">{t.mustTry || 'Must Try!'}</h3>
              <p className="text-orange-100 text-sm mt-1">
                {tray.some(item => !isBeverageCategory(item.category)) && !tray.some(item => isBeverageCategory(item.category))
                  ? t.suggestDrink || "How about adding a refreshing drink?"
                  : tray.some(item => isBeverageCategory(item.category)) && !tray.some(item => !isBeverageCategory(item.category))
                  ? t.suggestFood || "Would you like some delicious food with that?"
                  : t.perfectAdditions || "Perfect additions to your order!"
                }
              </p>
            </div>

            {/* Suggestions Grid */}
            <div className="p-4 overflow-y-auto max-h-[50vh]">
              <div className="grid grid-cols-2 gap-3">
                {suggestedItems.map(item => {
                  const qtyInTray = getItemQtyInTray(item.id);
                  return (
                    <div 
                      key={item.id}
                      className={`bg-slate-50 rounded-2xl overflow-hidden border ${item.is_promoted ? 'border-orange-300' : 'border-slate-100'}`}
                    >
                      <div className="aspect-square bg-slate-100 relative">
                        {item.image_url ? (
                          <img src={item.image_url} alt={getDishName(item)} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-3xl">
                            {isBeverageCategory(item.category) ? '' : ''}
                          </div>
                        )}
                        {qtyInTray > 0 && (
                          <div className="absolute top-1 right-1 w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md">
                            {qtyInTray}
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="flex items-start gap-1">
                          <h4 className="font-bold text-sm text-slate-900 line-clamp-1 flex-1">
                            {getDishName(item)}
                          </h4>
                          {item.is_promoted && (
                            <span className="flex-shrink-0 px-1 py-0.5 bg-orange-500 text-white text-[8px] font-bold rounded">
                              {t.mustTry || 'Must Try'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-orange-600 font-bold text-sm">{formatPrice(item.price)}</span>
                          {qtyInTray > 0 ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => {
                                  const trayItem = tray.find(t => t.id === item.id);
                                  if (trayItem) adjustQty(trayItem.instanceId, -1);
                                }}
                                className="w-7 h-7 bg-slate-200 rounded-lg flex items-center justify-center text-slate-700 font-bold text-sm"
                              >
                                −
                              </button>
                              <span className="w-5 text-center font-bold text-xs">{qtyInTray}</span>
                              <button
                                onClick={() => quickAddItem(item, null)}
                                className="w-7 h-7 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                              >
                                +
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => quickAddItem(item, null)}
                              className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold active:scale-90"
                            >
                              +
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-slate-100 bg-white">
              <div className="flex gap-3">
                <button
                  onClick={skipSuggestions}
                  className="flex-1 py-3.5 bg-slate-100 text-slate-700 rounded-2xl font-bold"
                >
                  {t.skip || 'Skip'}
                </button>
                <button
                  onClick={skipSuggestions}
                  className="flex-1 py-3.5 bg-slate-900 text-white rounded-2xl font-bold"
                >
                  {t.continueToOrder || 'Continue to Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* INGREDIENTS MODAL */}
      {showIngredientModal && selectedDish && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowIngredientModal(false)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-[2.5rem] rounded-t-[2.5rem] max-h-[85vh] overflow-hidden animate-slide-up" onClick={e => e.stopPropagation()}>
            {/* Header with Image */}
            <div className="relative h-48 bg-slate-100">
              {selectedDish.image_url ? (
                <img src={selectedDish.image_url} alt={getDishName(selectedDish)} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-6xl"></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <button 
                onClick={() => setShowIngredientModal(false)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg"
              >
                <X size={20} className="text-slate-700" />
              </button>
              <div className="absolute bottom-4 left-4 right-4">
                <h3 className="text-xl font-black text-white">{getDishName(selectedDish)}</h3>
                <p className="text-orange-400 font-bold">{formatPrice(selectedDish.price)}</p>
              </div>
            </div>

            {/* Ingredients */}
            <div className="p-5">
              <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">
                {t.ingredients || 'Ingredients'}
              </h4>
              {selectedDish.ingredients ? (
                <div className="flex flex-wrap gap-2">
                  {selectedDish.ingredients.split(',').map((ing, idx) => (
                    <span key={idx} className="px-3 py-1.5 bg-slate-100 rounded-full text-sm font-medium text-slate-700">
                      {ing.trim()}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm">{t.noIngredientsListed || 'No ingredients listed'}</p>
              )}

              {/* Add to Order Button */}
              <button
                onClick={() => { quickAddItem(selectedDish, null); setShowIngredientModal(false); }}
                className="w-full mt-6 bg-slate-900 text-white py-4 rounded-2xl font-bold text-base active:scale-[0.98] transition-transform"
              >
                {t.addToOrder || 'Add to Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PREPARATION OPTIONS MODAL - Custom Input Only */}
      {showPrepModal && pendingItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-md sm:rounded-[2.5rem] rounded-t-[2.5rem] max-h-[90vh] overflow-y-auto animate-slide-up">
            <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-100 flex items-center justify-between z-10 rounded-t-[2.5rem]">
              <div>
                <h3 className="font-black text-lg text-slate-900">{getDishName(pendingItem)}</h3>
                <p className="text-orange-600 font-bold">{formatPrice(pendingItem.price)}</p>
              </div>
              <button onClick={() => setShowPrepModal(false)} className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                <X size={18} className="text-slate-500" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Show Ingredients if available */}
              {pendingItem.ingredients && (
                <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t.ingredients || 'Ingredients'}</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {pendingItem.ingredients.split(',').map((ing, idx) => (
                      <span key={idx} className="px-2 py-1 bg-orange-50 border border-orange-200 rounded-full text-xs font-medium text-orange-700">
                        {ing.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Note Input Only */}
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t.specialRequest || 'Special Request / Notes'}</h4>
                <textarea
                  value={chefNote}
                  onChange={(e) => setChefNote(e.target.value)}
                  placeholder={t.specialRequestPlaceholder || "Enter any special requests, allergies, or preparation instructions..."}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  rows="4"
                />
                <p className="text-xs text-slate-400 mt-2">{t.specialRequestExamples || 'e.g., No spicy, Extra sauce, No onions, Allergy to nuts...'}</p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 flex gap-3">
              <button
                onClick={() => setShowPrepModal(false)}
                className="flex-1 py-3.5 bg-slate-100 text-slate-700 rounded-2xl font-bold"
                data-testid="cancel-prep-btn"
              >
                {t.cancel || 'Cancel'}
              </button>
              <button
                onClick={confirmItem}
                className="flex-1 py-3.5 bg-slate-900 text-white rounded-2xl font-bold"
                data-testid="confirm-prep-btn"
              >
                {t.addToOrder || 'Add to Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ORDER TRAY MODAL - Enhanced */}
      {showTrayModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-[2.5rem] rounded-t-[2.5rem] max-h-[90vh] flex flex-col animate-slide-up">
            <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-100 flex items-center justify-between rounded-t-[2.5rem]">
              <h3 className="font-black text-xl text-slate-900">{t.yourOrder || 'Your Order'}</h3>
              <button onClick={() => setShowTrayModal(false)} className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                <X size={18} className="text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {tray.map(item => (
                <div key={item.instanceId} className="bg-slate-50 rounded-[1.5rem] overflow-hidden">
                  {/* Item Header */}
                  <div className="p-4 bg-white border-b border-slate-100">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-bold text-slate-900">{getDishName(item)}</h4>
                        <p className="text-orange-600 font-bold text-sm">{formatPrice(item.price)} each</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => adjustQty(item.instanceId, -1)} className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-600 font-bold border">−</button>
                        <span className="w-6 text-center font-bold">{item.quantity}</span>
                        <button onClick={() => adjustQty(item.instanceId, 1)} className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold">+</button>
                      </div>
                    </div>
                  </div>

                  {/* Custom Notes Only */}
                  <div className="p-3">
                    <p className="text-xs font-bold text-slate-400 uppercase mb-2">{t.specialRequest || 'Special Request'}</p>
                    <input
                      type="text"
                      value={item.notes || ''}
                      onChange={(e) => updateTrayItemNote(item.instanceId, e.target.value)}
                      placeholder={t.enterSpecialRequests || "Enter any special requests..."}
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  {/* Item Footer */}
                  <div className="px-4 py-3 bg-white border-t border-slate-100 flex justify-between items-center">
                    <button onClick={() => removeItem(item.instanceId)} className="text-red-500 text-xs font-bold">{t.remove || 'Remove'}</button>
                    <span className="font-black text-slate-900">{formatPrice(item.price * item.quantity)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Table Selection & Total */}
            <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 space-y-4">
              {/* Table display — always pre-filled from the scanned QR */}
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase mb-2">
                  {t.yourTable || 'Your Table'}
                </p>
                {tablesLoaded && !isTableValid ? (
                  <div className="w-full px-4 py-3 bg-red-50 border border-red-200 rounded-2xl text-sm font-bold text-center text-red-700" data-testid="table-invalid-msg">
                    {t.invalidTable || `Table "${selectedTable}" is not valid. Please scan the QR on your table.`}
                  </div>
                ) : (
                  <div className="w-full px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-2xl text-lg font-bold text-center text-emerald-700" data-testid="table-number-display">
                    {t.table || 'Table'} {selectedTable}
                  </div>
                )}
              </div>

              {/* Total & Place Order */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 font-medium">{t.total || 'Total'}</p>
                  <p className="text-2xl font-black text-slate-900">{formatPrice(getTrayTotal())}</p>
                </div>
                <button
                  onClick={placeOrder}
                  disabled={!isTableValid || isSubmitting}
                  className={`px-8 py-4 rounded-2xl font-bold text-base transition-all flex items-center gap-2 ${
                    isTableValid && !isSubmitting
                      ? 'bg-slate-900 text-white active:scale-95'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                  data-testid="place-order-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t.processing || 'Processing...'}
                    </>
                  ) : (
                    t.placeOrder || 'Place Order'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EMAIL COLLECTION MODAL */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] p-6 max-w-sm w-full animate-slide-up">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-500" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-1">{t.orderPlaced || 'Order'} #{lastOrderNumber} {t.placed || 'Placed!'}</h3>
              <p className="text-slate-500 text-sm">{t.wantUpdates || 'Want to receive updates about your order?'}</p>
            </div>
            
            <div className="space-y-3">
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder={t.enterEmail || "Enter your email (optional)"}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <p className="text-xs text-slate-400 text-center">
                {t.emailInfo || "We'll send you preparation updates and your final bill"}
              </p>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={skipEmail}
                className="flex-1 py-3.5 bg-slate-100 text-slate-700 rounded-2xl font-bold"
              >
                {t.skip || 'Skip'}
              </button>
              <button
                onClick={submitEmail}
                className="flex-1 py-3.5 bg-orange-500 text-white rounded-2xl font-bold"
              >
                {customerEmail ? (t.submit || 'Submit') : (t.done || 'Done')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] p-8 text-center max-w-sm w-full animate-slide-up">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={40} className="text-green-500" />
            </div>
            <h3 className="text-2xl font-black text-slate-900 mb-2">{t.orderPlaced || 'Order Placed!'}</h3>
            <p className="text-slate-500">{t.orderSuccess || 'Your order is being prepared'}</p>
            {lastOrderNumber && (
              <p className="text-orange-600 font-bold mt-2">{t.orderNumber || 'Order'} #{lastOrderNumber}</p>
            )}
          </div>
        </div>
      )}

      {/* RCS BRANDING FOOTER */}
      <footer className="bg-gradient-to-b from-slate-900 to-slate-950 text-white py-12 mt-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Restaurant Info */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-amber-400 mb-2">BENZ EATERY</h2>
            <p className="text-slate-400 text-sm">
              {currentLang.code === 'vi' 
                ? 'Nha hang ngon nhat Phu Quoc - Phai thu khi den dao!' 
                : 'Best Restaurant in Phu Quoc - A Must-Try Experience!'}
            </p>
            <div className="flex items-center justify-center gap-2 mt-3 text-slate-500 text-sm">
              <MapPin size={14} />
              <span>Phu Quoc Island, Kien Giang, Vietnam</span>
            </div>
          </div>
          
          {/* SEO Rich Keywords Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 text-center text-xs text-slate-500">
            <div>
              <svg className="inline w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg> Best Restaurant Phu Quoc
            </div>
            <div>
              <svg className="inline w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg> Must-Try Vietnamese Food
            </div>
            <div>
              <svg className="inline w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg> Fresh Seafood Daily
            </div>
            <div>
              <svg className="inline w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg> Top Rated Dining
            </div>
          </div>
          
          {/* Divider */}
          <div className="border-t border-slate-800 pt-6">
            {/* RCS Branding */}
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <p className="text-slate-400 text-sm">
                Dev by{' '}
                <a 
                  href="https://rigeneconsultancy.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-amber-400 font-bold hover:text-amber-300 transition-colors"
                >
                  RCS
                </a>
              </p>
              <p className="text-slate-600 text-xs">
                © {new Date().getFullYear()} Benz Eatery. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Custom Styles */}
      <style dangerouslySetInnerHTML={{ __html: `
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
      `}} />
    </div>
  );
};

export default CustomerOrder;
